/**
 * Search utilities for text formatting and filtering
 * Shared between Search components and entity services
 */

/**
 * Remove accents from text for search normalization
 * @param {string} text - Text to normalize
 * @returns {string} Text without accents
 */
export const stripAccents = (text = '') => text.normalize?.('NFD').replaceAll(/([\u0300-\u036F])/g, '');

/**
 * Format input text for search matching
 * @param {string} text - Text to format
 * @returns {string} Formatted text
 */
export const formatInput = text => stripAccents(text)?.replaceAll('-', ' ').replaceAll("'", ' ').toLowerCase();

/**
 * Constants for search functionality
 */
export const SEARCH_CONFIG = {
  MIN_CHARACTERS: 1,
  MAX_SUGGESTIONS_PER_TYPE: 4,
  ENTITY_TYPES: {
    COMMUNE: 'COMMUNE',
    EPCI: 'EPCI',
  },
};

/**
 * Get the display group an entity belongs to
 * @param {Object|string} entity - Entity object
 * @returns {string} 'EPCI', 'Communes', or '' for a bare string
 */
export const getGroup = (entity) => {
  if (typeof entity === 'string') return '';
  return entity?.type === SEARCH_CONFIG.ENTITY_TYPES.EPCI ? 'EPCI' : 'Communes';
};

/**
 * Check whether any word of an entity's name starts with the query
 * For multi-word queries, checks if the sequence appears at word boundaries
 * @param {Object} entity - Entity with pre-normalized searchName/searchWords
 * @param {string} query - Already normalized search query
 * @returns {boolean} Whether any word starts with query
 */
export const anyWordStartsWith = (entity, query) => {
  const { searchName = '', searchWords = [] } = entity ?? {};

  // If query contains spaces, look for it at word boundaries
  if (query.includes(' ')) {
    return searchName.startsWith(query) || searchName.includes(` ${query}`);
  }

  // For single word queries, check if any word starts with it
  return searchWords.some(word => word.startsWith(query));
};

/**
 * Read an entity's code, falling back to an empty string when it is missing.
 * @param {Object} entity - Entity record
 * @returns {string} The code, or '' when undefined
 */
const codeOf = entity => entity.code || '';

/**
 * Read an entity's normalized name, falling back to an empty string.
 * getEntitiesIndex always sets searchName, but anyWordStartsWith and getBucket
 * both guard against its absence: the comparators guard it the same way rather
 * than being the one place that assumes it.
 * @param {Object} entity - Entity record
 * @returns {string} The normalized name, or '' when undefined
 */
const nameOf = entity => entity.searchName || '';

/**
 * Rank a filtered entity within its group for sorting.
 * Communes: 1 = C1 exact name, 2 = C2 name prefix, 3 = C3 non-first-word match.
 * EPCI:     1 = E1 exact word,  2 = E2 word prefix.
 * Ranks are only ever compared within a group (getGroup splits them later).
 * @param {Object} entity - Entity with pre-normalized searchName/searchWords
 * @param {string} query - Already normalized search query
 * @returns {number} Bucket rank (lower sorts first), Infinity if no match
 */
export const getBucket = (entity, query) => {
  const { searchName = '', searchWords = [] } = entity ?? {};

  if (getGroup(entity) === 'EPCI') {
    if (searchWords.some(word => word === query)) return 1; // E1: exact word
    return anyWordStartsWith(entity, query) ? 2 : Infinity; // E2: word prefix
  }

  if (searchName === query) return 1; // C1: exact name
  if (searchName.startsWith(query)) return 2; // C2: name prefix
  return anyWordStartsWith(entity, query) ? 3 : Infinity; // C3: non-first word
};

/**
 * Filter and rank entities for the search autocomplete
 *
 * Reads searchName/searchWords, normalized once in getEntitiesIndex, rather
 * than re-normalizing every entity on each keystroke.
 * @param {Array} entities - Entities from the search index
 * @param {Object} params - Filter parameters
 * @param {string} params.inputValue - Raw input value
 * @returns {Array} Communes first, then EPCI. Communes are capped at
 *   MAX_SUGGESTIONS_PER_TYPE unless the query is a perfect match, in which case
 *   every homonym is returned (issue #228); EPCI are always capped.
 */
export const filterOptions = (entities, { inputValue }) => {
  const formatted = formatInput(inputValue);
  if (formatted.length < SEARCH_CONFIG.MIN_CHARACTERS) {
    return [];
  }

  // Match if any word of the name starts with the query, or the code does
  const filtered = entities.filter(entity =>
    anyWordStartsWith(entity, formatted) || (entity?.code || '').startsWith(formatted)
  );

  // A fully numeric query is a code search (postal code or SIREN); anything
  // else is a name search.
  const isCodeSearch = /^\d+$/.test(formatted);

  // Code mode sorts by code ascending, then by name; name mode ranks by
  // bucket (C1/C2/C3, E1/E2), then name, then code. The bucket rank is
  // pre-computed ONCE, not inside the comparator: recomputing it O(n log n)
  // times would blow the frame budget on wide queries ("s" filters 8k+
  // communes). The code tie-break keeps overseas communes reachable (#155).
  const sorted = isCodeSearch
    ? filtered.toSorted((a, b) =>
        codeOf(a).localeCompare(codeOf(b)) || nameOf(a).localeCompare(nameOf(b))
      )
    : filtered
        .map(entity => ({ entity, bucket: getBucket(entity, formatted) }))
        .toSorted((a, b) =>
          a.bucket - b.bucket ||
          nameOf(a.entity).localeCompare(nameOf(b.entity)) ||
          codeOf(a.entity).localeCompare(codeOf(b.entity))
        )
        .map(r => r.entity);

  // Perfect match lifts the commune cap so every homonym stays visible.
  const communeMatches = sorted.filter(entity => getGroup(entity) === 'Communes');

  const exactNameHomonyms = communeMatches.filter(entity => nameOf(entity) === formatted);
  // A postal code is a perfect match only once fully typed: 5 digits (rule 1a)
  const postalHomonyms =
    isCodeSearch && formatted.length === 5
      ? communeMatches.filter(entity => codeOf(entity) === formatted)
      : [];

  let communes;
  if (!isCodeSearch && exactNameHomonyms.length > SEARCH_CONFIG.MAX_SUGGESTIONS_PER_TYPE) {
    // More than 4 exact-name homonyms: show them all, drop C2/C3
    communes = exactNameHomonyms;
  } else if (postalHomonyms.length > 0) {
    // Fully typed postal code: show every commune of that code
    communes = postalHomonyms;
  } else {
    communes = communeMatches.slice(0, SEARCH_CONFIG.MAX_SUGGESTIONS_PER_TYPE);
  }

  // EPCI stay capped at 4 and sit below the communes (decision 1b)
  const epcis = sorted
    .filter(entity => getGroup(entity) === 'EPCI')
    .slice(0, SEARCH_CONFIG.MAX_SUGGESTIONS_PER_TYPE);

  return [...communes, ...epcis];
};
